import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Variant } from 'src/app/models/variant.model';
import { RequestService } from 'src/app/services/request.service';
import { nameExistsValidator } from '../../validators/name-exists-validator';

const locomotivePath = 'assets/locomotive.svg';

@Component({
  selector: 'app-add-wagon-variant',
  templateUrl: './add-wagon-variant.component.html',
  styleUrls: ['./add-wagon-variant.component.scss']
})
export class AddWagonVariantComponent implements OnInit, OnDestroy {
  private unscubscribe$: Subject<void> = new Subject<void>();
  private missmatchWarningTextPreviousAndNext: string = 'The width of this wagon is different compared to the previous and next wagon.';
  private missmatchWarningTextPrevious: string = 'The width of this wagon is different compared to the previous wagon.';
  private missmatchWarningTextNext: string = 'The width of this wagon is different compared to the next wagon.';

  selectedOption: number = 1;
  variantForm: FormGroup;
  selectedVariant: any;
  rotated: boolean = false;
  selectedWagon: any;
  isWarningToShow: boolean;
  warningMessage: string = '';
  variantImageSource: string | ArrayBuffer | null = null;
  locomotiveImageSource: string | ArrayBuffer | null = locomotivePath;
  imgToAdd: any;
  train: any;
  variantList: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<AddWagonVariantComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private requestService: RequestService
  ) { }

  ngOnInit() {
    this.train = this.data.train;

    let existingWagonNames = [];
    if (!this.train.trainConfiguration) {
      this.train.trainConfiguration = []
    }
    else {
      for (let index = 0; index < this.train.trainConfiguration.length; index++) {
        const wagon = this.train.trainConfiguration[index];
        existingWagonNames.push(wagon.name);
      }
    }

    this.variantForm = new FormGroup({
      name: new FormControl(null, [Validators.required, Validators.maxLength(100), nameExistsValidator(existingWagonNames)])
    });

    // Mark the field as touched programaticly to indicate any errors beforehand.
    // Otherwise regardless the specified validators the first error occurence will pop up after the user left the focus of the input.
    this.variantForm.get('name').markAsTouched();

    if (this.selectedWagon && !this.selectedWagon.variantList) {
      this.variantList = this.selectedWagon.variantList;
    }
  }

  radioButtonChanged(value: number) {
    this.selectedOption = value;
  }

  variantSelectionChanged() {
    this.rotated = false;

    this.requestService.getVariant(this.selectedVariant.id)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(next => {
        this.imgToAdd = next.floor1Base64String;
        this.variantImageSource = 'data:image/png;base64,' + next.floor1Base64String
      });
  }

  loadVariants() {
    this.requestService.getVariants(this.selectedWagon.id)
      .pipe(takeUntil(this.unscubscribe$))
      .subscribe(variants => {
        this.setVariantList(variants);
      });

    this.checkWagonDimensions();
  }

  setVariantList(variants: Variant[]) {
    this.selectedVariant = undefined;
    this.variantList = [];

    if (variants) {
      this.variantList = variants;
    }
  }

  checkWagonDimensions(): void {
    this.isWarningToShow = false;
    this.warningMessage = '';

    let previousTrainPart = undefined;
    let nextTrainPart = undefined;

    if (this.data.index === 0 && this.train.trainConfiguration && this.train.trainConfiguration.lenght === 0) {
      return;
    }

    let previousIndex = this.data.index - 1;

    if (previousIndex >= 0) {
      previousTrainPart = this.train.trainConfiguration[previousIndex];
    }

    let nextIndex = this.data.index + 1;

    if (nextIndex > this.train.trainConfiguration.lenght - 1) {
      nextIndex = this.train.trainConfiguration.lenght - 1;
    }

    nextTrainPart = this.train.trainConfiguration[nextIndex];

    if (previousTrainPart && previousTrainPart.wagonWidth && nextTrainPart && nextTrainPart.wagonWidth) {
      let previousWidthMissmatch = previousTrainPart.wagonWidth !== this.selectedWagon.ySize;
      let nextWidthMissmatch = nextTrainPart.wagonWidth !== this.selectedWagon.ySize;

      if (previousWidthMissmatch && nextWidthMissmatch) {
        this.isWarningToShow = true;
        this.warningMessage = this.missmatchWarningTextPreviousAndNext;
      } else if (previousWidthMissmatch) {
        this.isWarningToShow = true;
        this.warningMessage = this.missmatchWarningTextPrevious;
      } else if (nextWidthMissmatch) {
        this.isWarningToShow = true;
        this.warningMessage = this.missmatchWarningTextNext;
      }
    }
    else if (previousTrainPart && previousTrainPart.wagonWidth) {
      let previousWidthMissmatch = previousTrainPart.wagonWidth !== this.selectedWagon.ySize;

      if (previousWidthMissmatch) {
        this.isWarningToShow = true;
        this.warningMessage = this.missmatchWarningTextPrevious;
      }
    }
    else if (nextTrainPart && nextTrainPart.wagonWidth) {
      let nextWidthMissmatch = nextTrainPart.wagonWidth !== this.selectedWagon.ySize;

      if (nextWidthMissmatch) {
        this.isWarningToShow = true;
        this.warningMessage = this.missmatchWarningTextNext;
      }
    }
  }

  rotate() {
    this.rotated = !this.rotated
  }

  save() {
    this.addTrainPartToTrainConfiguration();
    this.dialogRef.close(this.train);
  }

  cancel() {
    this.dialogRef.close();
  }

  addTrainPartToTrainConfiguration() {
    let newTrainPart = {
      "name": this.variantForm.get('name').value.trim(),
      "trainId": this.train.id,
      "variantId": null,
      "index": this.data.index,
      "rotated": false,
      "wagonWidth": null,
      "image": null,
    };

    if (this.selectedOption === 1) {
      newTrainPart = {
        "name": this.variantForm.get('name').value.trim(),
        "trainId": this.train.id,
        "variantId": this.selectedVariant.id,
        "index": this.data.index,
        "rotated": this.rotated,
        "wagonWidth": this.selectedWagon.ySize,
        "image": this.imgToAdd,
      };
    }

    let newTrainConfiguration = [...this.train.trainConfiguration];
    newTrainConfiguration.splice(newTrainPart.index, 0, newTrainPart);

    for (let index = 0; index < newTrainConfiguration.length; index++) {
      const trainPart = newTrainConfiguration[index];
      trainPart.index = index;
    }

    this.train.trainConfiguration = newTrainConfiguration;
  }

  ngOnDestroy(): void {
    this.unscubscribe$.next();
    this.unscubscribe$.complete();
  }
}
